/**
 * Pure decisions behind the four front-office pages, kept out of the components
 * so they can be asserted directly — the shape `packages/admin/src/pages/access/
 * accessView.ts` established for the Access page.
 *
 * Most of them are contract facts rather than presentation:
 *
 *   - `frontOfficeNavEntries` decides what the sidebar's Front office group
 *     offers. Every entry is gated on a permission **code** read from the
 *     server's own projection, never on a role name, so a site that regrants a
 *     code moves the surface with it (BMS-spec `docs/grms/specification.md`
 *     §14.2). The group is shown when any entry is.
 *   - `visitActions` reads the **stored** `status`, never the effective one.
 *     `no_show` and `overstayed` are derived from the clock and nothing sets
 *     them, so a control offered on their basis would be a control for a
 *     transition that does not exist (`docs/access/visitors.md`).
 *   - `cardListQuery` decides what the **server** filters and what is left to
 *     the client. `GET /access/cards` returns 50 rows by default and 200 at
 *     most, so a filter applied only in the browser would search a page rather
 *     than a site. Only the two derived statuses stay client-side, because the
 *     column does not store them (`docs/access/cards.md` §Statuses).
 *   - `canReEncode` decides which card records may be written to a chip again.
 *   - `scheduleWindowsIssue` and `occupancyPatch` decide what Hotel settings is
 *     allowed to send. Both routes are `.strict()` and refuse a body that names
 *     anything else, so the shape is worked out here and asserted, rather than
 *     discovered as a `400` at a front desk.
 *
 * The revoke-reason routing is deliberately **not** here: it is
 * `revokeIntent` in `@bms/common`, shared with Admin's Cards tab, because a
 * second copy of "which route does `lost` go to" is the drift this page would
 * otherwise reintroduce.
 */

import { ALARM_TYPE_OPTIONS, MAX_SCHEDULE_WINDOWS, PERMISSIONS, alarmHistoryRange } from '@bms/common';
import type {
  AccessCard,
  AccessSchedule,
  AccessScheduleWindow,
  AlarmHistoryEntry,
  AlarmHistoryQueryParams,
  AlarmHistoryResult,
  AlarmRangeDays,
  AlarmType,
  CardListFilters,
  CardStatus,
  CardType,
  EffectiveCardStatus,
  GrmsOccupancyAutomationSettings,
  OccupancyAutomationPatch,
  RoomScene,
  Visit,
} from '@bms/common';
import { siteDayNumber, siteDayStart } from '@/components/checkin/deskRules';
import { english, type Translate } from './translate';

// ---------------------------------------------------------------------------
// The Front office nav group
// ---------------------------------------------------------------------------

export interface FrontOfficeNavEntry {
  key: string;
  path: string;
  /**
   * The English fixture. `NavLinks.tsx` (the sidebar, owned elsewhere) reads
   * `labelKey` through `t()` instead — `label` stays for a caller that has not
   * moved onto it yet, and for `frontOfficeView.test.ts`'s synthetic entries.
   */
  label: string;
  /**
   * `nav.<key>` in common's `nav` table — see `labelKey`'s own note above.
   * Optional so `frontOfficeView.test.ts`'s synthetic, permission-only entries
   * need not carry one.
   */
  labelKey?: string;
  /** Every code this entry needs. All of them, not any of them. */
  requiredPermissions: readonly string[];
  /**
   * At least one of these, when the list is non-empty.
   *
   * Hotel settings is the entry that forced this: it holds two independent
   * sections, Access hours and Occupancy automation, each gated on its own
   * narrow write code (BMS-spec `docs/grms/specification.md` §14.2), and a
   * manager may hold either. Listing both in `requiredPermissions` would hide
   * the page from someone who holds one; listing one would lose the other half
   * of the audience entirely. The rule is genuinely "any", so it is said as
   * "any" rather than approximated.
   */
  anyPermissions?: readonly string[];
}

/**
 * The group's entries in order.
 *
 * The group is defined as a list so adding a page is a row rather than a new
 * gate — the sidebar renders whatever this says and nothing else decides who
 * sees what.
 */
export const FRONT_OFFICE_NAV: readonly FrontOfficeNavEntry[] = [
  {
    key: 'visitors',
    path: '/visitors',
    label: 'Visitors',
    labelKey: 'nav.visitors',
    requiredPermissions: [PERMISSIONS.VISITORS_READ],
  },
  {
    key: 'cards',
    path: '/cards',
    label: 'Cards',
    labelKey: 'nav.cards',
    requiredPermissions: [PERMISSIONS.CARDS_READ],
  },
  {
    // `access_analytics:read` decides who the page is for (F3: the
    // receptionist reads rooms and alarms but is not a reporting audience).
    // The pair stays because the page's two reads — the hotel KPIs of §11.6
    // and the alarm history of §9.3 — are gated on them server-side, and an
    // entry must never open a page that answers 403.
    key: 'reports',
    path: '/reports',
    label: 'Reports',
    labelKey: 'nav.reports',
    requiredPermissions: [PERMISSIONS.ACCESS_ANALYTICS_READ, PERMISSIONS.GRMS_READ, PERMISSIONS.ALARMS_READ],
  },
  {
    // Either write code. Each section then gates itself on its own, so a
    // manager granted one narrow permission gets one section rather than a page
    // with a refusal in half of it.
    key: 'hotel-settings',
    path: '/hotel-settings',
    label: 'Hotel settings',
    labelKey: 'nav.hotel-settings',
    requiredPermissions: [],
    anyPermissions: [
      PERMISSIONS.ACCESS_CONTROL_WRITE_SCHEDULE_WINDOWS,
      PERMISSIONS.SITE_WRITE_OCCUPANCY_AUTOMATION,
    ],
  },
];

/**
 * The entries this identity may see.
 *
 * `has` is `usePermissions().has`, which fails closed: an absent, stale or
 * wrong-site projection is not a grant, so an unavailable projection hides the
 * whole group rather than offering a page that will answer 403.
 */
export function frontOfficeNavEntries(
  has: (permission: string) => boolean,
  entries: readonly FrontOfficeNavEntry[] = FRONT_OFFICE_NAV,
): FrontOfficeNavEntry[] {
  return entries.filter((entry) => {
    // Fail closed on an entry that names no code at all. `[].every()` is true
    // and an empty `anyPermissions` is skipped, so without this line an entry
    // added with both lists empty would be a page offered to everyone — a
    // housekeeping attendant included. `frontOfficeView.test.ts` asserts the
    // union of the two lists is non-empty for every entry in FRONT_OFFICE_NAV;
    // this is the same rule enforced where the decision is actually made,
    // rather than only on the shape of the data.
    if (entry.requiredPermissions.length === 0 && !entry.anyPermissions?.length) return false;
    return entry.requiredPermissions.every((permission) => has(permission))
      && (!entry.anyPermissions?.length || entry.anyPermissions.some((permission) => has(permission)));
  });
}

/** The group is shown when any of its entries is. */
export function showFrontOfficeGroup(
  has: (permission: string) => boolean,
  entries: readonly FrontOfficeNavEntry[] = FRONT_OFFICE_NAV,
): boolean {
  return frontOfficeNavEntries(has, entries).length > 0;
}

// ---------------------------------------------------------------------------
// Visits
// ---------------------------------------------------------------------------

export interface VisitActions {
  checkIn: boolean;
  checkOut: boolean;
  cancel: boolean;
  /** A card can be issued while the visit has not reached a terminal status. */
  issueCard: boolean;
}

/**
 * Which controls a visit row may offer.
 *
 * Read from the stored `status`. `checked_out` and `cancelled` are terminal, so
 * nothing leaves them; `no_show` and `overstayed` never appear here because
 * they are not stored statuses at all.
 */
export function visitActions(visit: Pick<Visit, 'status'>): VisitActions {
  return {
    checkIn: visit.status === 'expected',
    checkOut: visit.status === 'checked_in',
    cancel: visit.status === 'expected',
    issueCard: visit.status === 'expected' || visit.status === 'checked_in',
  };
}

/**
 * The visit list's range, as the API wants it.
 *
 * Both bounds empty is not "everything": the route answers with the current
 * site-local day, which is what a front desk means by the word. The `to` bound
 * is widened to the end of its day so picking one day twice includes it.
 */
export function visitRangeQuery(
  fromIso: string | undefined,
  toIso: string | undefined,
): { from?: string; to?: string } {
  const query: { from?: string; to?: string } = {};
  if (fromIso) query.from = fromIso;
  if (toIso) {
    const end = new Date(toIso);
    if (!Number.isNaN(end.getTime())) {
      query.to = new Date(end.getFullYear(), end.getMonth(), end.getDate(), 23, 59, 59, 999).toISOString();
    }
  }
  return query;
}

// ---------------------------------------------------------------------------
// Cards
// ---------------------------------------------------------------------------

export interface CardFilterState {
  /** Case-insensitive substring of the holder name. Asked of the server. */
  holderName: string;
  /** A whole UID. Asked of the server, which matches it exactly. */
  cardUid: string;
  cardType: CardType | 'all';
  status: EffectiveCardStatus | 'all';
}

export const EMPTY_CARD_FILTERS: CardFilterState = {
  holderName: '',
  cardUid: '',
  cardType: 'all',
  status: 'all',
};

/**
 * The desk's opening view (F15, A9): active guest keys. A holder of `cards:read`
 * without `cards:write_staff` (the receptionist) opens here; "All cards" is
 * `EMPTY_CARD_FILTERS`, one click away.
 */
export const DESK_CARD_FILTERS: CardFilterState = {
  ...EMPTY_CARD_FILTERS,
  cardType: 'guest',
  status: 'active',
};

/**
 * Whether the Cards page is the guest-key desk rather than the site register:
 * guest / active defaults, rows grouped by room, the chip column hidden and the
 * UID as the row's hover text. Decided by the code, never a role name (R6).
 */
export function isGuestKeyDesk(has: (permission: string) => boolean): boolean {
  return !has(PERMISSIONS.CARDS_WRITE_STAFF);
}

export interface CardsByRoom {
  /** The cards in display order: by the room they open, cards with no room last. */
  ordered: AccessCard[];
  /** The first card of each room's group → that room's code (`null`: no room). */
  groupStarts: Map<string, string | null>;
}

/** Groups the page under the room each card opens (`roomCode`), keeping the server's order inside a room. */
export function cardsByRoom(cards: readonly AccessCard[]): CardsByRoom {
  const ordered = cards
    .map((card, index) => ({ card, index }))
    .sort((a, b) => {
      const ra = a.card.roomCode ?? null;
      const rb = b.card.roomCode ?? null;
      if (ra !== rb) {
        if (ra === null) return 1;
        if (rb === null) return -1;
        const byRoom = ra.localeCompare(rb, undefined, { numeric: true });
        if (byRoom !== 0) return byRoom;
      }
      return a.index - b.index;
    })
    .map(({ card }) => card);
  const groupStarts = new Map<string, string | null>();
  let previous: string | null | undefined;
  for (const card of ordered) {
    const room = card.roomCode ?? null;
    if (room !== previous) groupStarts.set(card.id, room);
    previous = room;
  }
  return { ordered, groupStarts };
}

/** The stored statuses. The other two are worked out at read time. */
const STORED_CARD_STATUSES = ['pending', 'active', 'suspended', 'revoked'] as const;

function isStoredStatus(status: CardFilterState['status']): status is CardStatus {
  return (STORED_CARD_STATUSES as readonly string[]).includes(status);
}

/** The page size asked for. The route caps at 200; asking for the cap is deliberate. */
export const CARD_PAGE_SIZE = 200;

/**
 * The filters the **server** applies.
 *
 * This matters more than it looks: `GET /access/cards` returns 50 rows by
 * default and 200 at most, ordered newest first. A page that fetched the
 * default and then searched in the browser would search 50 cards out of a
 * hotel's several thousand and quietly report "no cards match" for a card that
 * exists — which is precisely the question this page is opened to answer.
 *
 * `cardUid` is an equality match server-side (`ac.card_uid = $n`), not a
 * substring, so it is sent only when a whole UID has been typed and the control
 * says so. `holderName` is `ILIKE %…%`. A derived status is not sent at all:
 * the column stores four values and `expired` / `not_yet_valid` are neither.
 */
export function cardListQuery(filters: CardFilterState): CardListFilters {
  const holderName = filters.holderName.trim();
  const cardUid = filters.cardUid.trim();
  return {
    ...(holderName ? { holderName } : {}),
    ...(cardUid ? { cardUid } : {}),
    ...(filters.cardType === 'all' ? {} : { cardType: filters.cardType }),
    ...(isStoredStatus(filters.status) ? { status: filters.status } : {}),
    limit: CARD_PAGE_SIZE,
  };
}

/**
 * The one refinement the server cannot make.
 *
 * `expired` and `not_yet_valid` are computed from the card's own window each
 * time it is read (BMS-spec `docs/access/cards.md` §Statuses), so they are
 * applied here, over the page the server already narrowed with everything else.
 */
export function refineByDerivedStatus(
  cards: readonly AccessCard[],
  status: CardFilterState['status'],
): AccessCard[] {
  if (status === 'all' || isStoredStatus(status)) return [...cards];
  return cards.filter((card) => card.effectiveStatus === status);
}

/** Whether the returned page is at the cap, so the list may not be the whole answer. */
export function isCardPageTruncated(returned: number): boolean {
  return returned >= CARD_PAGE_SIZE;
}

export function cardsSummary(cards: readonly AccessCard[], tr: Translate = english): string {
  const active = cards.filter((card) => card.effectiveStatus === 'active').length;
  const pending = cards.filter((card) => card.status === 'pending').length;
  const revoked = cards.filter((card) => card.status === 'revoked').length;
  const parts = [
    tr('cardDesk.frontOfficeSummaryTotal', { count: cards.length }),
    tr('cardDesk.frontOfficeSummaryActive', { count: active }),
  ];
  if (pending > 0) parts.push(tr('cardDesk.frontOfficeSummaryPending', { count: pending }));
  parts.push(tr('cardDesk.frontOfficeSummaryRevoked', { count: revoked }));
  return parts.join(' · ');
}

/**
 * Whether this card record may be written to a chip again.
 *
 * Two situations need it and neither is a new issuance: a `pending` card whose
 * physical write failed or was never confirmed, and an active card whose chip is
 * being rewritten — a replacement chip for the same record, or a card that was
 * demagnetised. A revoked or retired record is never re-encoded: it is dead, and
 * writing it would put a credential the gateways already deny into a hand.
 *
 * Re-encoding reads the stored record through
 * `POST /access/cards/:cardId/payload` and confirms with `/written`; it never
 * composes access, so it is not subject to the role-template rule.
 */
export function canReEncode(card: Pick<AccessCard, 'status'>): boolean {
  return card.status === 'pending' || card.status === 'active' || card.status === 'suspended';
}

// ---------------------------------------------------------------------------
// Reports
//
// Two reads on one page: the hotel KPIs of BMS-spec
// `docs/grms/specification.md` §11.6 over 7, 30 or 90 days, and the alarm
// history of §9.3. Read-only, both — this page reports what happened and
// controls nothing, and no room control appears on it.
// ---------------------------------------------------------------------------

export interface ReportFilterState {
  /**
   * The KPI window, and the alarm list's window unless a custom one is set.
   *
   * `GET /grms/analytics` takes exactly 7, 30 or 90 — the tiles cannot answer
   * for an arbitrary range, so the preset is the shared control and the custom
   * range below narrows the list alone rather than pretending the tiles moved.
   */
  days: AlarmRangeDays;
  /** ISO instants. Both, or neither: one bound alone is not a range. */
  customFrom?: string;
  customTo?: string;
  /**
   * The exclusive end of a range set by a site day on the chart. The date fields
   * speak the viewer's wall clock, so their `customTo` is widened in that zone;
   * a site day ends where the site says, which on a desk elsewhere is not the same.
   */
  customEnd?: string;
  alarmType: AlarmType | 'all';
  /** One room's whole code. The route matches it exactly, so a part of one finds nothing. */
  roomCode?: string;
}

export const DEFAULT_REPORT_FILTERS: ReportFilterState = { days: 30, alarmType: 'all' };

/** Whether the list is on a custom range rather than following the KPI preset. */
export function hasCustomRange(filters: ReportFilterState): boolean {
  return !!filters.customFrom && !!filters.customTo;
}

/**
 * The alarm page this desk asks for.
 *
 * 200 rather than the route's default 50: a hotel's ninety days of alarms is
 * more than fifty rows, and a page that quietly showed the newest fifty would
 * report "that alarm never happened" for one that did. The cap is 500; asking
 * for 200 leaves headroom and keeps the request cheap.
 *
 * `status` is deliberately not offered as a filter. The list's value here is
 * *what happened over a period*, and hiding the resolved ones — nearly all of
 * them — leaves a page that answers a different question than its title.
 */
export const ALARM_HISTORY_PAGE_SIZE = 200;

export function alarmHistoryQuery(
  filters: ReportFilterState,
  now: Date = new Date(),
): AlarmHistoryQueryParams {
  const preset = alarmHistoryRange(filters.days, now);
  // `to` is exclusive on `created_at`, so a day picked as the end of the range
  // is widened to the instant after it. Picking "the 9th" twice must include
  // the 9th, and with a midnight bound it would include none of it.
  const customTo = filters.customTo ? new Date(filters.customTo) : null;
  const custom = hasCustomRange(filters) && customTo && !Number.isNaN(customTo.getTime())
    ? {
      from: filters.customFrom as string,
      to: filters.customEnd
        ?? new Date(customTo.getFullYear(), customTo.getMonth(), customTo.getDate() + 1).toISOString(),
    }
    : null;
  const { from, to } = custom ?? preset;
  const roomCode = filters.roomCode?.trim();
  return {
    from,
    to,
    ...(filters.alarmType === 'all' ? {} : { type: filters.alarmType }),
    ...(roomCode ? { roomCode } : {}),
    limit: ALARM_HISTORY_PAGE_SIZE,
  };
}

/**
 * The same question from a later row.
 *
 * Built from the first page's query rather than asked afresh: its `to` is the
 * instant of the first read, so an alarm raised since cannot push a row already
 * loaded onto the next page and show it twice.
 */
export function alarmHistoryPage(query: AlarmHistoryQueryParams, offset: number): AlarmHistoryQueryParams {
  return offset > 0 ? { ...query, offset } : query;
}

export interface AlarmDayGroup {
  /** `siteDayNumber` of the group's rows: the site's calendar day, never the browser's. */
  day: number;
  alarms: AlarmHistoryEntry[];
}

/** The rows under the site day each was raised on, newest day first, keeping the route's order inside a day. */
export function groupAlarmsByDay(
  alarms: readonly AlarmHistoryEntry[],
  timeZone: string | undefined,
): AlarmDayGroup[] {
  const byDay = new Map<number, AlarmHistoryEntry[]>();
  for (const alarm of alarms) {
    const day = siteDayNumber(alarm.createdAt, timeZone);
    const rows = byDay.get(day);
    if (rows) rows.push(alarm);
    else byDay.set(day, [alarm]);
  }
  return [...byDay.entries()]
    .map(([day, rows]) => ({ day, alarms: rows }))
    .sort((left, right) => right.day - left.day);
}

export interface ResolvedStamp {
  at: string;
  /** Resolved on another site day than it was raised, so the time alone would misread as the row's day. */
  withDay: boolean;
}

/** How a resolved time prints inside its day group, or `null` while the alarm is open. */
export function resolvedStamp(
  alarm: Pick<AlarmHistoryEntry, 'createdAt' | 'resolvedAt'>,
  timeZone: string | undefined,
): ResolvedStamp | null {
  if (!alarm.resolvedAt) return null;
  return {
    at: alarm.resolvedAt,
    withDay: siteDayNumber(alarm.resolvedAt, timeZone) !== siteDayNumber(alarm.createdAt, timeZone),
  };
}

/**
 * How long an open alarm has stood, in whole seconds, or `null` for a row that
 * has a duration. The route computes `durationSeconds` only on resolving; an
 * open alarm's age is worked out here against the reader's clock, and floored
 * at zero if that clock runs behind the server's.
 */
export function openElapsedSeconds(
  alarm: Pick<AlarmHistoryEntry, 'createdAt' | 'durationSeconds'>,
  now: Date,
): number | null {
  if (alarm.durationSeconds !== null) return null;
  const raised = new Date(alarm.createdAt).getTime();
  if (Number.isNaN(raised)) return null;
  return Math.max(0, Math.floor((now.getTime() - raised) / 1000));
}

/**
 * The sentence above the list.
 *
 * It counts `total`, which the route computes over the whole filtered range and
 * not over the page, so the sentence cannot contradict the rows under it. The
 * open and severity counts exist only for the rows loaded so far (`result.alarms`,
 * every page), so while those are short of the total the sentence says so rather
 * than let an open alarm on a later page read as none.
 */
export function alarmHistorySummary(
  result: AlarmHistoryResult | undefined,
  filters: ReportFilterState,
  tr: Translate = english,
): string | undefined {
  if (!result) return undefined;
  const loaded = result.alarms.length;
  const partial = result.total > loaded;
  const open = result.alarms.filter((alarm) => alarm.status !== 'resolved').length;
  const window = hasCustomRange(filters)
    ? tr('reports.frontOfficeWindowChosen')
    : tr('reports.frontOfficeWindowDays', { days: filters.days });
  const parts = [tr('reports.frontOfficeSummaryCount', { count: result.total, window })];
  if (open > 0) {
    parts.push(partial
      ? tr('reports.frontOfficeStillOpenOfLoaded', { count: open, loaded })
      : tr('reports.frontOfficeStillOpen', { count: open }));
  }
  if (loaded > 0) {
    const severities = tr('reports.severityCounts', { ...severityCounts(result.alarms) });
    parts.push(partial ? `${severities} ${tr('reports.ofLoaded', { loaded })}` : severities);
  }
  return parts.join(' · ');
}

export interface SeverityCounts {
  warning: number;
  critical: number;
  info: number;
}

/** A severity this build does not name counts as `info`, the way the row draws it. */
export function severityCounts(alarms: readonly Pick<AlarmHistoryEntry, 'severity'>[]): SeverityCounts {
  const counts: SeverityCounts = { warning: 0, critical: 0, info: 0 };
  for (const alarm of alarms) {
    if (alarm.severity === 'warning' || alarm.severity === 'critical') counts[alarm.severity] += 1;
    else counts.info += 1;
  }
  return counts;
}

export interface AlarmDaySlot {
  day: number;
  count: number;
}

/**
 * One slot per site day of a half-open window, a day with no alarm included, and
 * the rows counted under the site day each was raised on. A row outside the
 * window has no slot: the route's bounds and the analytics route's come from two
 * clocks and can differ by moments.
 */
export function alarmsPerDay(
  alarms: readonly Pick<AlarmHistoryEntry, 'createdAt'>[],
  from: string,
  to: string,
  timeZone: string | undefined,
): AlarmDaySlot[] {
  const first = siteDayNumber(from, timeZone);
  const last = siteDayNumber(new Date(new Date(to).getTime() - 1), timeZone);
  const slots = Array.from({ length: Math.max(0, last - first + 1) }, (_, index) => ({ day: first + index, count: 0 }));
  for (const alarm of alarms) {
    const slot = slots[siteDayNumber(alarm.createdAt, timeZone) - first];
    if (slot) slot.count += 1;
  }
  return slots;
}

/** The top of a count axis: the next clean even number, so half of it is a whole count too. */
export function countAxisMax(peak: number): number {
  if (peak <= 2) return 2;
  const magnitude = 10 ** Math.floor(Math.log10(peak));
  const step = [1, 2, 4, 5, 6, 8, 10].find((multiple) => {
    const candidate = multiple * magnitude;
    return candidate >= peak && candidate % 2 === 0;
  });
  return (step ?? 10) * magnitude;
}

/** A row's type for the ranked bars. A type this build does not name is `other`, as in the list. */
export const OTHER_ALARM_TYPE = 'other';

export interface AlarmTypeCount {
  type: AlarmType | typeof OTHER_ALARM_TYPE;
  count: number;
}

export interface AlarmTypeBreakdown {
  /** Largest first; a tie keeps the order the type filter lists them in. */
  ranked: AlarmTypeCount[];
  /** The known types with no row, for the one caption line that names them. */
  absent: AlarmType[];
}

export function alarmTypeBreakdown(alarms: readonly Pick<AlarmHistoryEntry, 'alarmType'>[]): AlarmTypeBreakdown {
  const known = ALARM_TYPE_OPTIONS.map((option) => option.value);
  const counts = new Map<AlarmTypeCount['type'], number>();
  for (const alarm of alarms) {
    const type = known.includes(alarm.alarmType) ? alarm.alarmType : OTHER_ALARM_TYPE;
    counts.set(type, (counts.get(type) ?? 0) + 1);
  }
  const order = [...known, OTHER_ALARM_TYPE] as AlarmTypeCount['type'][];
  return {
    ranked: order
      .filter((type) => counts.has(type))
      .map((type) => ({ type, count: counts.get(type) as number }))
      .sort((left, right) => right.count - left.count),
    absent: known.filter((type) => !counts.has(type)),
  };
}

/** The list narrowed to one site day, as a click on the per-day chart asks for it. */
export function siteDayRange(
  day: number,
  timeZone: string | undefined,
): Pick<ReportFilterState, 'customFrom' | 'customTo' | 'customEnd'> {
  const start = siteDayStart(day, timeZone);
  return { customFrom: start, customTo: start, customEnd: siteDayStart(day + 1, timeZone) };
}

/** The site day a custom range covers when it covers exactly one, so the chart can mark it. */
export function selectedReportDay(filters: ReportFilterState, timeZone: string | undefined): number | null {
  if (!hasCustomRange(filters)) return null;
  const day = siteDayNumber(filters.customFrom as string, timeZone);
  return day === siteDayNumber(filters.customTo as string, timeZone) ? day : null;
}

/** The median time to resolve of the rows that have one, or `null` with none (audit K4). */
export function medianResolutionSeconds(
  alarms: readonly Pick<AlarmHistoryEntry, 'durationSeconds'>[],
): number | null {
  const durations = alarms
    .flatMap((alarm) => (alarm.durationSeconds === null ? [] : [alarm.durationSeconds]))
    .sort((left, right) => left - right);
  if (durations.length === 0) return null;
  const middle = Math.floor(durations.length / 2);
  return durations.length % 2 === 1 ? durations[middle] : (durations[middle - 1] + durations[middle]) / 2;
}

// ---------------------------------------------------------------------------
// Hotel settings › Access hours
//
// The narrow edit of BMS-spec `docs/access/schedules.md` §Editing windows
// without owning the schedule. A schedule decides *which* doors and holders are
// restricted and *whether* the restriction applies; its windows decide only
// *when*, and that is hotel policy.
// ---------------------------------------------------------------------------

export interface Weekday {
  value: number;
  short: string;
  long: string;
}

// A Monday, used only to ask Intl for what each weekday is called — never read
// as a real date, and UTC so the local timezone cannot roll it onto Sunday or
// Tuesday and name the wrong day.
const A_MONDAY_UTC = Date.UTC(2024, 0, 1);

/**
 * ISO-8601 weekday numbers, 1 = Monday … 7 = Sunday, in reading order, named
 * for `locale` through `Intl.DateTimeFormat` rather than a translation table —
 * a new locale therefore needs no key added here.
 */
export function weekdays(locale: string): Weekday[] {
  const shortFormat = new Intl.DateTimeFormat(locale, { weekday: 'short', timeZone: 'UTC' });
  const longFormat = new Intl.DateTimeFormat(locale, { weekday: 'long', timeZone: 'UTC' });
  return Array.from({ length: 7 }, (_, index) => {
    const date = new Date(A_MONDAY_UTC + index * 86_400_000);
    return { value: index + 1, short: shortFormat.format(date), long: longFormat.format(date) };
  });
}

const TIME_OF_DAY = /^([01][0-9]|2[0-3]):[0-5][0-9](:[0-5][0-9])?$/;

/**
 * Why this window list cannot be sent, or `null` when it can.
 *
 * Every rule here is one Edge enforces, restated so Save can be disabled with a
 * reason instead of a round trip ending in a refusal a desk cannot act on. Two
 * of them are easy to get backwards and are spelled out:
 *
 *   - An **empty list is refused**, not accepted as "closed". "No hours" shuts
 *     a door or a cardholder out entirely, which is a lock-status or attachment
 *     decision and stays with `access_control:write`. To close a door, a
 *     technician disables or detaches the schedule.
 *   - `end` **before** `start` is not an error: that window wraps past midnight,
 *     and `end` equal to `start` covers the whole 24 hours. Neither is rejected
 *     here, because neither is rejected by the route.
 */
export function scheduleWindowsIssue(
  windows: readonly AccessScheduleWindow[],
  tr: Translate = english,
): string | null {
  if (windows.length === 0) {
    return tr('hotelSettings.frontOfficeIssueEmptyList');
  }
  if (windows.length > MAX_SCHEDULE_WINDOWS) {
    return tr('hotelSettings.frontOfficeIssueTooMany', { max: MAX_SCHEDULE_WINDOWS });
  }
  for (const [index, window] of windows.entries()) {
    if (window.days.length === 0) return tr('hotelSettings.frontOfficeIssueNoDays', { index: index + 1 });
    if (!TIME_OF_DAY.test(window.start)) return tr('hotelSettings.frontOfficeIssueBadStart', { index: index + 1 });
    if (!TIME_OF_DAY.test(window.end)) return tr('hotelSettings.frontOfficeIssueBadEnd', { index: index + 1 });
  }
  return null;
}

/**
 * The body for `PATCH /access/schedules/:scheduleCode/windows`.
 *
 * Exactly `{ windows }` and nothing else. The route is `.strict()`, so a body
 * that also carried `enabled` — the obvious thing to send back after reading a
 * schedule — is `400 invalid_request` rather than an edit with one field
 * ignored. Each window is narrowed to its three members for the same reason: a
 * stored window read back and re-sent whole would carry whatever the projection
 * gains next.
 *
 * Times are normalised to `HH:MM` and days sorted, so this is also what
 * `windowsChanged` compares. Without that the route answering `07:00:00` and
 * the form holding `07:00` would read as an edit, and Save would be offered on
 * a schedule nobody had touched.
 */
export function scheduleWindowsBody(
  windows: readonly AccessScheduleWindow[],
): { windows: AccessScheduleWindow[] } {
  return {
    windows: windows.map((window) => ({
      days: [...window.days].sort((left, right) => left - right),
      start: shortTime(window.start),
      end: shortTime(window.end),
    })),
  };
}

/** `HH:MM:SS` read back from the route, shown as `HH:MM`; anything else untouched. */
export function shortTime(value: string): string {
  return /^\d{2}:\d{2}:\d{2}$/.test(value) ? value.slice(0, 5) : value;
}

/**
 * One window as a sentence, including the two shapes that look like mistakes.
 *
 * A desk reading "22:00–06:00" needs to be told it runs into the next day, and
 * "00:00–00:00" needs to be told it is open all day rather than closed.
 */
export function describeWindow(
  window: AccessScheduleWindow,
  tr: Translate = english,
  locale = 'en',
): string {
  const weekdayList = weekdays(locale);
  const days = [...window.days].sort((left, right) => left - right);
  const names = days.map((day) => weekdayList.find((weekday) => weekday.value === day)?.short ?? String(day));
  const consecutive = days.length > 2 && days.every((day, index) => index === 0 || day === days[index - 1] + 1);
  const dayLabel = days.length === 7
    ? tr('hotelSettings.frontOfficeEveryDay')
    : consecutive ? `${names[0]}–${names[names.length - 1]}` : names.join(', ');
  const start = shortTime(window.start);
  const end = shortTime(window.end);
  if (start === end) return tr('hotelSettings.frontOfficeWindowAllDay', { days: dayLabel });
  const wraps = end < start;
  return tr(
    wraps ? 'hotelSettings.frontOfficeWindowRangeNextDay' : 'hotelSettings.frontOfficeWindowRange',
    { days: dayLabel, start, end },
  );
}

/** A schedule's windows as an editable draft — a copy, so cancelling restores the stored list. */
export function windowDraft(schedule: Pick<AccessSchedule, 'windows'>): AccessScheduleWindow[] {
  return schedule.windows.map((window) => ({
    days: [...window.days],
    start: shortTime(window.start),
    end: shortTime(window.end),
  }));
}

/** Whether the draft differs from what is stored, so Save is offered only when it does. */
export function windowsChanged(
  stored: readonly AccessScheduleWindow[],
  draft: readonly AccessScheduleWindow[],
): boolean {
  return JSON.stringify(scheduleWindowsBody(stored)) !== JSON.stringify(scheduleWindowsBody(draft));
}

// ---------------------------------------------------------------------------
// Hotel settings › Occupancy automation
//
// BMS-spec `docs/grms/specification.md` §8.4.1 and §14.2. Whether the mechanism
// exists and whether it is `enabled` is site configuration and stays with the
// technician on `PATCH /api/site`; what temperature a checked-in room settles
// at, and which scene greets the guest, is hotel policy and is this.
// ---------------------------------------------------------------------------

/** `grms-occupancy-automation.schema.json`. Refused, never clamped. */
export const SETPOINT_MIN_C = 5;
export const SETPOINT_MAX_C = 35;
const SCENE_CODE = /^[a-z0-9_-]{1,64}$/;

export interface OccupancyDraft {
  comfortSetpointC: string;
  welcomeSceneCode: string;
  ecoSetpointC: string;
  lightsOffSceneCode: string;
}

export const EMPTY_OCCUPANCY_DRAFT: OccupancyDraft = {
  comfortSetpointC: '',
  welcomeSceneCode: '',
  ecoSetpointC: '',
  lightsOffSceneCode: '',
};

/**
 * The stored object as an editable draft.
 *
 * Absent and empty collapse deliberately: a missing `welcome_scene_code` means
 * "no scene on check-in", and an empty field is how that is offered back — the
 * patch below turns an emptied field into the `null` that removes the key.
 */
export function occupancyDraft(
  settings: GrmsOccupancyAutomationSettings | undefined,
): OccupancyDraft {
  if (!settings) return { ...EMPTY_OCCUPANCY_DRAFT };
  return {
    comfortSetpointC: settings.check_in?.comfort_setpoint_c?.toString() ?? '',
    welcomeSceneCode: settings.check_in?.welcome_scene_code ?? '',
    ecoSetpointC: settings.vacancy?.eco_setpoint_c?.toString() ?? '',
    lightsOffSceneCode: settings.vacancy?.lights_off_scene_code ?? '',
  };
}

/** Why this draft cannot be sent, or `null`. The route's own rules, said locally. */
export function occupancyDraftIssue(draft: OccupancyDraft, tr: Translate = english): string | null {
  const setpoint = (value: string, label: string): string | null => {
    if (value.trim() === '') return null;
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) return tr('hotelSettings.frontOfficeIssueMustBeNumber', { label });
    if (parsed < SETPOINT_MIN_C || parsed > SETPOINT_MAX_C) {
      return tr('hotelSettings.frontOfficeIssueMustBeBetween', { label, min: SETPOINT_MIN_C, max: SETPOINT_MAX_C });
    }
    return null;
  };
  const scene = (value: string, label: string): string | null =>
    value.trim() === '' || SCENE_CODE.test(value.trim())
      ? null
      : tr('hotelSettings.frontOfficeIssueSceneCode', { label });

  return setpoint(draft.comfortSetpointC, tr('hotelSettings.frontOfficeCheckInComfortLabel'))
    ?? setpoint(draft.ecoSetpointC, tr('hotelSettings.frontOfficeVacancyEcoLabel'))
    ?? scene(draft.welcomeSceneCode, tr('hotelSettings.frontOfficeWelcomeSceneFieldLabel'))
    ?? scene(draft.lightsOffSceneCode, tr('hotelSettings.frontOfficeLightsOffSceneFieldLabel'));
}

/**
 * The body for `PATCH /api/site/occupancy-automation`: only what changed.
 *
 * Three rules the route makes and this keeps:
 *
 *   - **`enabled` is never sent.** It is not in the type and not in the draft;
 *     naming it is `400 invalid_request`, because whether the mechanism runs is
 *     site configuration and this permission does not carry it.
 *   - **A cleared scene code is `null`, not `''`.** `null` is the documented way
 *     to remove an action; an empty string fails the scene-code pattern and the
 *     whole request is refused. A cleared *setpoint* has no such spelling and is
 *     left alone instead — removing a setpoint is not something the route offers.
 *   - **An unchanged field is omitted**, so an edit to one setpoint cannot
 *     re-assert three stale values read a minute earlier.
 */
export function occupancyPatch(
  settings: GrmsOccupancyAutomationSettings | undefined,
  draft: OccupancyDraft,
): OccupancyAutomationPatch {
  const stored = occupancyDraft(settings);
  const patch: OccupancyAutomationPatch = {};

  const checkIn: NonNullable<OccupancyAutomationPatch['check_in']> = {};
  if (draft.comfortSetpointC.trim() !== stored.comfortSetpointC && draft.comfortSetpointC.trim() !== '') {
    checkIn.comfort_setpoint_c = Number(draft.comfortSetpointC);
  }
  if (draft.welcomeSceneCode.trim() !== stored.welcomeSceneCode) {
    checkIn.welcome_scene_code = draft.welcomeSceneCode.trim() === '' ? null : draft.welcomeSceneCode.trim();
  }
  if (Object.keys(checkIn).length > 0) patch.check_in = checkIn;

  const vacancy: NonNullable<OccupancyAutomationPatch['vacancy']> = {};
  if (draft.ecoSetpointC.trim() !== stored.ecoSetpointC && draft.ecoSetpointC.trim() !== '') {
    vacancy.eco_setpoint_c = Number(draft.ecoSetpointC);
  }
  if (draft.lightsOffSceneCode.trim() !== stored.lightsOffSceneCode) {
    vacancy.lights_off_scene_code = draft.lightsOffSceneCode.trim() === '' ? null : draft.lightsOffSceneCode.trim();
  }
  if (Object.keys(vacancy).length > 0) patch.vacancy = vacancy;

  return patch;
}

/** Whether there is anything to save. An empty patch is not sent. */
export function occupancyPatchIsEmpty(patch: OccupancyAutomationPatch): boolean {
  return Object.keys(patch).length === 0;
}

/**
 * Why the editor is read-only, or `null` when it is editable.
 *
 * Two distinct situations, and telling them apart is the point: with nothing
 * stored the route answers `409 resource_state_conflict` and a site
 * administrator has to create the object first; with it stored but disabled the
 * setpoints are saveable and simply inert, which is worth saying rather than
 * pretending the fields do something today.
 */
export function occupancyReadOnlyReason(
  settings: GrmsOccupancyAutomationSettings | undefined,
  canWrite: boolean,
  tr: Translate = english,
): string | null {
  if (!settings) {
    return tr('hotelSettings.frontOfficeNotConfigured');
  }
  if (!canWrite) {
    // In words, not the permission code (UX-30).
    return tr('hotelSettings.frontOfficeNeedsWritePermission');
  }
  return null;
}

// ---------------------------------------------------------------------------
// Scene codes
// ---------------------------------------------------------------------------

export interface SceneCodeOption {
  sceneCode: string;
  sceneName: string;
  /** How many of the sampled rooms define it. */
  rooms: number;
}

/**
 * The scene codes the site's rooms actually define, with how many define each.
 *
 * The count is shown rather than hidden because it is the failure this page
 * would otherwise cause: the automation activates the scene *per room*
 * (`activateScene` in `src/services/grmsAutomation.ts` looks the code up in that
 * room's `room_scenes`), so a code present in eight rooms of forty is a welcome
 * scene that silently does nothing in thirty-two of them.
 *
 * Read off the room-detail projection, which carries `scenes` under
 * `rooms:read`, and **not** from `GET /rooms/:room/scenes` — that route wants
 * `scenes:read`, which a `front_office_manager` deliberately does not hold
 * (GRMS §10.5).
 */
export function sceneCodeOptions(
  perRoom: readonly (readonly RoomScene[] | undefined)[],
): SceneCodeOption[] {
  const byCode = new Map<string, SceneCodeOption>();
  for (const scenes of perRoom) {
    const list = scenes ?? [];
    for (const sceneCode of new Set(list.map((scene) => scene.sceneCode))) {
      const scene = list.find((candidate) => candidate.sceneCode === sceneCode);
      const found = byCode.get(sceneCode);
      if (found) found.rooms += 1;
      else byCode.set(sceneCode, { sceneCode, sceneName: scene?.sceneName ?? sceneCode, rooms: 1 });
    }
  }
  return [...byCode.values()].sort((left, right) =>
    right.rooms - left.rooms || left.sceneCode.localeCompare(right.sceneCode));
}

/**
 * The options a scene select offers, with the stored code always among them.
 *
 * A code stored before a room was retemplated may no longer be defined
 * anywhere. Dropping it from the list would silently rewrite the setting to
 * something else the moment anything else on the form was saved, so it is kept
 * and marked as defined in no room.
 */
export function sceneOptionsIncluding(
  options: readonly SceneCodeOption[],
  storedCode: string,
): SceneCodeOption[] {
  const code = storedCode.trim();
  if (code === '' || options.some((option) => option.sceneCode === code)) return [...options];
  return [...options, { sceneCode: code, sceneName: code, rooms: 0 }];
}
